import { DeleteOutlined, InboxOutlined, PlusOutlined } from "@ant-design/icons";
import { useMutation } from "@tanstack/react-query";
import { Button, Col, DatePicker, Form, Image, Input, InputNumber, message, Modal, Row, Select, Space, Upload } from "antd";
import axios from "axios";
import { forwardRef, useImperativeHandle, useState } from "react";
import { hostname, ISO8601 } from "../hooks/useEndeavour";
import ReactQuill from "react-quill";
import dayjs from "dayjs";
import RulesSelector from "./RulesSelector";
const { TextArea } = Input;

const { Dragger } = Upload;

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["blockquote", "link"],
  ],
};

const UpdateCampaignModal = forwardRef((props, ref) => {
  const [visible, setVisible] = useState(false);
  const [bannerFiles, setBannerFiles] = useState([]);
  const [backdropFiles, setBackdropFiles] = useState([]);
  const [form] = Form.useForm();
  const [items, setItems] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [data, setData] = useState();

  const openModal = (data) => {
    const rulesId = data?.rules?.map((rule) => rule.id) || data?.rules_id || [];
    const description = data?.description || "";
    const licenseMatch = description.match(/Trade Promotion Competition Licence No.:\s*([^<]+)/);
    const licenseNumber = licenseMatch ? licenseMatch[1].trim() : "";
    form.setFieldsValue({
      start_date: data?.start_at ? ISO8601(data.start_at, true).split(" ")[0] : undefined,
      expired_date: data?.expired_at ? ISO8601(data.expired_at, true).split(" ")[0] : undefined,
      name: data?.name,
      type: data?.type,
      max_participants: data?.max_participants,
      max_winners: data?.max_winners,
      description: description.replace(/<p>Trade Promotion Competition Licence No.:.*?<\/p>\s*|Trade Promotion Competition Licence No.:[^<]*/g, ""),
      achievement: data?.achievement,
      venue: data?.venue,
      terms: data?.terms ? data.terms.join(",") : "",
      rules_id: rulesId,
      license_number: licenseNumber,
    });
    setData(data);
    setSelectedSlots(data?.slots || []);

    setVisible(true);
  };
  const closeModal = () => {
    setVisible(false);
    form.resetFields();
    setData(null);
    setInputValue("");
    setBannerFiles([]);
    setBackdropFiles([]);
    setItems([]);
  };

  useImperativeHandle(ref, () => ({
    openModal,
    closeModal,
  }));

  const AddingSlots = () => {
    let newSlot = form.getFieldValue("slots");
    if (!newSlot) {
      message.error("Please enter a slot before adding.");
      return;
    }
    newSlot = dayjs(newSlot).format("YYYY-MM-DD");
    if (selectedSlots.includes(newSlot)) {
      message.error("This slot already exists.");
      return;
    }
    setSelectedSlots((prevSlots) => [...prevSlots, newSlot]);
    form.setFieldsValue({ slots: "" });
  };

  const submit = useMutation({
    mutationFn: (data) => {
      return axios
        .post(`${hostname}/pa-alliance/campaign/event`, data, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        })
        .then((res) => {
          if (res.status != 200) {
            message.error("Upload Fail");
          }
          setLoading(false);
          closeModal();
          props.onChange();
        })
        .finally(() => {
          setLoading(false);
        });
    },
  });
  const getBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64Data = reader.result.split(",")[1];
        resolve(base64Data);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const getBannerDraggerConfig = () => ({
    name: "banner",
    multiple: false,
    fileList: bannerFiles,
    showUploadList: true,
    beforeUpload: async (file) => {
      const base64 = await getBase64(file);
      const currentFile = {
        uid: file.uid,
        name: file.name,
        type: file.type,
        size: file.size,
        base64: base64,
      };
      setBannerFiles([currentFile]);
      return false;
    },
    onRemove: (file) => {
      handleBannerRemove(file);
    },
  });

  const getBackdropDraggerConfig = () => ({
    name: "backdrop",
    multiple: false,
    fileList: backdropFiles,
    showUploadList: true,
    beforeUpload: async (file) => {
      const base64 = await getBase64(file);
      const currentFile = {
        uid: file.uid,
        name: file.name,
        type: file.type,
        size: file.size,
        base64: base64,
      };
      setBackdropFiles([currentFile]);
      return false;
    },
    onRemove: (file) => {
      handleBackdropRemove(file);
    },
  });

  const handleBannerRemove = (file) => {
    setBannerFiles((prevFiles) => prevFiles.filter((f) => f.uid !== file.uid));
  };

  const handleBackdropRemove = (file) => {
    setBackdropFiles((prevFiles) => prevFiles.filter((f) => f.uid !== file.uid));
  };

  const onSubmit = (values) => {
    let id = data.id;
    form.validateFields().then((values) => {
      setLoading(true);

      let data = [];
      for (let i = items.length - 1; i >= 0; i--) {
        data.push({
          id: items[i].id,
          name: items[i].content,
        });
      }

      if (bannerFiles.length > 0) {
        values.file = bannerFiles[0].base64;
        values.file_name = bannerFiles[0].name;
        values.file_type = bannerFiles[0].type;
      }

      if (backdropFiles.length > 0) {
        values.backdrop_file = backdropFiles[0].base64;
        values.backdrop_file_name = backdropFiles[0].name;
        values.backdrop_file_type = backdropFiles[0].type;
      }
      values.id = id;
      delete values.backdrop_url;
      delete values.image;
      // Replace old license line and prepend new one
      if (values.license_number) {
        const withoutLicense = values.description.replace(/<p>Trade Promotion Competition Licence No.:.*?<\/p>\s*|Trade Promotion Competition Licence No.:[^<]*/g, "");
        values.description = `<p>Trade Promotion Competition Licence No.: ${values.license_number}</p>` + withoutLicense;
        delete values.license_number;
      }
      // values.slots = values.slots.split(",");
      values.slots = selectedSlots;
      values.terms = [values.terms];
      submit.mutate(values);
    });
  };

  return (
    <Modal title="Update Campaign" open={visible} onCancel={closeModal} footer={null}>
      <Form layout="vertical" form={form} onFinish={onSubmit}>
        <Form.Item label="Campaign name" name="name" rules={[{ required: true, message: "Please input the campaign name!" }]}>
          <Input value={inputValue} onChange={(e) => setInputValue(e.target.value)} />
        </Form.Item>

        <Form.Item label="Campaign Type" name="type" rules={[{ required: true, message: "Please select the campaign type!" }]}>
          <Select
            options={[
              { value: "subscription", label: "Subscription" },
              { value: "lucky_draw", label: "Lucky Draw" },
            ]}
          />
        </Form.Item>

        <Form.Item label="Start Date" name="start_date" rules={[{ required: true, message: "Please select the start date!" }]}>
          <Input type="date" />
        </Form.Item>

        <Form.Item label="Expired Date" name="expired_date" rules={[{ required: true, message: "Please select the expired date!" }]}>
          <Input type="date" />
        </Form.Item>

        <Form.Item label="Venue" name="venue" rules={[{ required: true, message: "Please enter venue!" }]}>
          <Input style={{ width: "100%" }} />
        </Form.Item>

        <Row gutter={8}>
          <Col span={20}>
            <Form.Item label="Slots" name="slots">
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label=" ">
              <Button block onClick={AddingSlots} type="primary" icon={<PlusOutlined />}>
                Add
              </Button>
            </Form.Item>
          </Col>
        </Row>
        {selectedSlots.length > 0 && (
          <div style={{ padding: "0px 0px 0px 0px", borderRadius: 8, borderColor: "#d9d9d9", marginBottom: 20 }}>
            {selectedSlots.map((slot, index) => (
              <Row key={index} gutter={8} style={{ marginBottom: "8px", borderColor: "white white #d9d9d9 white ", borderStyle: "solid" }}>
                <Col span={20}>
                  <Space style={{ width: "100%", justifyContent: "left", alignItems: "center" }}>{slot}</Space>
                </Col>
                <Col span={4}>
                  <Button
                    size="small"
                    block
                    type="link"
                    onClick={() => {
                      setSelectedSlots((prevSlots) => prevSlots.filter((_, i) => i !== index));
                    }}
                  >
                    <DeleteOutlined /> Delete
                  </Button>
                </Col>
              </Row>
            ))}
          </div>
        )}

        <Form.Item noStyle shouldUpdate={(prevValues, curValues) => prevValues.type !== curValues.type}>
          {({ getFieldValue }) => {
            const type = getFieldValue("type");
            if (type === "subscription") {
              return (
                <Form.Item label="Max Participants" name="max_participants" rules={[{ required: true, message: "Please enter max participants!" }]}>
                  <InputNumber min={0} style={{ width: "100%" }} />
                </Form.Item>
              );
            }
            if (type === "lucky_draw") {
              return (
                <>
                  <Form.Item label="Max Winner" name="max_winners" rules={[{ required: true, message: "Please enter max winner!" }]}>
                    <InputNumber min={0} style={{ width: "100%" }} />
                  </Form.Item>
                  <Form.Item label="License Number" name="license_number" rules={[{ required: true, message: "Please enter license number!" }]}>
                    <Input placeholder="Enter license number" />
                  </Form.Item>
                </>
              );
            }
            return null;
          }}
        </Form.Item>

        <Form.Item label="Rules" name="rules_id">
          <RulesSelector />
        </Form.Item>

        <Form.Item label="Achievement" name="achievement" rules={[{ required: true, message: "Please enter achievement!" }]}>
          <InputNumber min={0} style={{ width: "100%" }} />
        </Form.Item>

        <Form.Item label="Description" name="description" rules={[{ required: true, message: "Please enter description!" }]}>
          <ReactQuill modules={quillModules} style={{ height: 200, marginBottom: 40 }} />
        </Form.Item>

        <Form.Item label="T&C" name="terms" rules={[{ required: true }]}>
          <ReactQuill modules={quillModules} style={{ height: 200, marginBottom: 40 }} />
        </Form.Item>

        <Form.Item name="image" label="Banner Image">
          <Image src={data?.banner_url} />
          <Dragger {...getBannerDraggerConfig()}>
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">Click or drag file to this area to upload</p>
            <p className="ant-upload-hint">
              Support for a single or bulk upload. Strictly prohibited from uploading company data or other banned files.
            </p>
          </Dragger>
        </Form.Item>
        <Form.Item name="backdrop_url" label="Backdrop Image">
          <Image src={data?.backdrop_url} />
          <Dragger {...getBackdropDraggerConfig()}>
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">Click or drag file to this area to upload</p>
            <p className="ant-upload-hint">
              Support for a single or bulk upload. Strictly prohibited from uploading company data or other banned files.
            </p>
          </Dragger>
        </Form.Item>

        <Form.Item>
          <Button type="primary" htmlType="submit" loading={loading}>
            Submit
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
});

export default UpdateCampaignModal;
